<script setup lang="ts">
import {useDashboardStore, useLocalizationStore} from '@/store';
import {
  type QForm,
  useQuasar
} from 'quasar';
import {useRouter} from 'vue-router';
import {useI18n} from 'vue-i18n';
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  reactive,
  ref,
  watch
} from 'vue';
import {to12HourTimeString} from '@/utils/dateUtil.ts';
import {
  feetInchesToMillimeters, millimetersToCentimeters,
  gramsToKilos, kilosToGrams,
  gramsToPounds, poundsToGrams
} from '@/utils/mathUtil.ts';

import {type CanaryPatientHeightWeight} from '@/types/webContracts.ts';

import {handleApiRequest} from '@/api/handleApiRequest.ts';
import {
  getCanaryHeightWeight,
  getCanarySampleTimezone,
  updateCanaryHeightWeight,
  updateCanarySampleTimezone
} from '@/api/endpoints/patientWeb.ts';

import MultiViewLayout from '@/components/layouts/MultiViewLayout.vue';
import TheContactModal from '@/components/TheContactModal.vue';
import BedtimeSelectionModal from '@/pages/TheSettings/components/BedtimeSelectionModal.vue';

interface PatientDetailsModel {
  bedtime: string | undefined;
  height: number | string | undefined;
  weight: number | string | undefined;
}

const dashboardStore = useDashboardStore(),
  localizationStore = useLocalizationStore(),
  $q = useQuasar(),
  router = useRouter(),
  {t} = useI18n();

const defBedtime = '9:00 pm';

const form = ref<QForm | null>(null),
  formIsDirty = ref<boolean>(false),
  isLoading = ref<boolean>(false),
  isUnmounted = ref<boolean>(false),
  showBedtimeModal = ref<boolean>(false),
  showContactModal = ref<boolean>(false);

const formModel = reactive({
  bedtime: undefined,
  height: undefined,
  weight: undefined
} as PatientDetailsModel);

const displayBedtime = computed(() => formModel.bedtime ? to12HourTimeString(formModel.bedtime) : undefined),
  showEditBedtime = computed(() => dashboardStore.patient !== null && dashboardStore.patient.hasPersonaIQ && formModel.bedtime);

const heightOpts = computed(() => {
  const heightFt = [2, 3, 4, 5, 6, 7],
    heightIn = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

  let options: { label: string, value: number }[] = [];

  heightFt.forEach(foot => {
    heightIn.forEach(inch => {
      options.push({
        label: `${foot}' ${inch}"`,
        value: feetInchesToMillimeters(foot, inch)
      });
    });
  });

  return options;
});

function getCanaryPatientHeightWeight(): CanaryPatientHeightWeight | undefined {
  if (formModel.height && formModel.weight) {
    if(localizationStore.isMetric){
      return {
        height: (typeof formModel.height === 'number') ? formModel.height * 10 : parseFloat(formModel.height) * 10,
        weight: kilosToGrams(formModel.weight)
      }
    }
    else {
      return {
        height: (typeof formModel.height === 'string') ? parseFloat(formModel.height) : formModel.height,
        weight: (typeof formModel.weight === 'string') ? poundsToGrams(parseFloat(formModel.weight) ) :  poundsToGrams(formModel.weight)
      }
    }
  }
  return;
}

function pushToSettingsView() {
  router.push({name: 'Settings'});
}

async function onFormSubmit() {
  $q.loading.show();

  const heightWeight = getCanaryPatientHeightWeight(),
    isValid = await form.value?.validate();

  if (isValid) {
    let updateHeightWeight,
      updateBedtime;

    // Update Height & Weight
    if (heightWeight) {
      updateHeightWeight = await handleApiRequest(() => updateCanaryHeightWeight(heightWeight), null, $q);
    }

    // Update Bedtime
    if (formModel.bedtime) {
      updateBedtime = await handleApiRequest(() => updateCanarySampleTimezone(formModel.bedtime!), null, $q);
    }

    // Success
    if (heightWeight && updateHeightWeight?.result && !formModel.bedtime
        || formModel.bedtime && updateBedtime?.result && !heightWeight
        || heightWeight && updateHeightWeight?.result && formModel.bedtime && updateBedtime?.result) {

      // Show success notification.
      $q.notify({type: 'message', message: t('reset_password_success')});

      // Return user to Settings view.
      pushToSettingsView();
    }
  }
  $q.loading.hide();
}

function updateModelBedtime(hour: string) {
  formModel.bedtime = hour;
}

onMounted(async () => {
  isLoading.value = true;

  const [
    getHeightWeightResponse,
    getBedtimeResponse
  ] = await Promise.all([
    handleApiRequest(() => getCanaryHeightWeight(), null, $q),
    handleApiRequest(() => getCanarySampleTimezone(), null, $q)
  ]);

  if (getHeightWeightResponse?.result) {
    const getHeightWeightResult = getHeightWeightResponse.result;

    if (!localizationStore.isMetric) {
      // Convert height to ft. & in. object.
      if (getHeightWeightResult.height > 0) formModel.height = getHeightWeightResult.height;

      // Convert weight to lbs.
      if (getHeightWeightResult.weight > 0) formModel.weight = gramsToPounds(getHeightWeightResult.weight);
    } else {
      // Convert height to decimal centimeters.
      if (getHeightWeightResult.height > 0) formModel.height = millimetersToCentimeters(getHeightWeightResult.height);

      // Convert weight to kilos.
      if (getHeightWeightResult.weight > 0) formModel.weight = gramsToKilos(getHeightWeightResult.weight);
    }

  }

  if (getBedtimeResponse?.result) {
    const bedtimeResponse = getBedtimeResponse.result.bedtime;

    formModel.bedtime = bedtimeResponse ? bedtimeResponse : '21:00';
  }

  isUnmounted.value = false;

  await nextTick(() => isLoading.value = false);
});

onUnmounted(() => {
  isUnmounted.value = true;

  form.value = null;
  formModel.bedtime = undefined;
  formModel.height = undefined;
  formModel.weight = undefined;
  isLoading.value = false;

  formIsDirty.value = false;
});

watch(formModel, () => {
  if (!isUnmounted.value && !isLoading.value) formIsDirty.value = true;
});
</script>

<template>
  <q-page padding>
    <MultiViewLayout :box-padding="$zb.enums.BoxPaddingEnum.p303048" :loading="isLoading"
                     :on-back-click="pushToSettingsView">

      <!-- Section Title -->
      <template #title>{{ t('settings') }}</template>

      <!-- Title Text -->
      <template #heading>{{ t('gen_patient_details') }}</template>

      <!-- Auxiliary -->
      <template #auxiliary>
        <zbm-skeleton-loader class="ml-auto" :loading="isLoading" size="24px">

          <!-- Question Circle Button -->
          <zbm-btn-icon @click="showContactModal = true" data-testid="help" icon="fa-regular fa-circle-question"
                        size="24px" text-color="content-txt-ttl"/>

          <!-- The Contact Modal -->
          <TheContactModal v-model="showContactModal"/>
        </zbm-skeleton-loader>
      </template>

      <!-- View Unique Content -->
      <template #default>
        <zbm-async-content :loading="isLoading">

          <!-- Loading Animations -->
          <template #loading>
            <q-skeleton class="mb-20" height="24px" width="100%"/>
            <q-skeleton class="mb-24" height="66px" width="300px"/>
            <q-skeleton class="mb-24" height="66px" width="300px"/>
            <q-skeleton class="mb-48" height="66px" width="300px"/>
            <q-skeleton class="mt-auto" height="44px" width="100%"/>
          </template>

          <!-- Content -->
          <template #default>

            <!-- Form -->
            <q-form @submit="onFormSubmit" class="column full-height" ref="form">

              <!-- Instructions -->
              <div class="row mb-20">
                <div class="col">
                  <p>{{ t('patient_details_body') }}</p>
                </div>
              </div>

              <!-- Height -->
              <div class="row mb-24">
                <div class="col" v-if="!localizationStore.isMetric">
                  <zbm-select-with-validation v-model="formModel.height" data-testid="patientHeight" for="name"
                                              :label="t('piq_height')" name="height" option-label="label"
                                              :options="heightOpts" option-value="value"
                                              :rules="[$zb.validators.required()]"/>
                </div>
                <div class="col" v-else>
                  <zbm-input-with-validation v-model="formModel.height" data-testid="patientHeight" for="name"
                                             :label="t('piq_height') +' ('+t('unit_abbrev_distance_centimeter_metric')+')'"
                                             name="height" max="241.3" min="61.0" step="0.1"
                                             :rules="[$zb.validators.required(), $zb.validators.betweenVal(61.0, 241.3)]"
                                             type="number"/>
                </div>
              </div>

              <!-- Weight -->
              <div :class="['row', formModel.bedtime ? 'mb-24' : 'mb-48']">
                <div class="col" v-if="!localizationStore.isMetric">
                  <zbm-input-with-validation v-model.number="formModel.weight" data-testid="patientWeight" for="weight"
                                             :label="t('piq_weight')" max="2000" min="1" name="weight" placeholder="0"
                                             :rules="[$zb.validators.required(), $zb.validators.betweenVal(1, 2000)]"
                                             type="number"/>
                </div>
                <div class="col" v-else>
                  <zbm-input-with-validation v-model.number="formModel.weight" data-testid="patientWeight" for="weight"
                                             :label="t('piq_weight') + ' ('+  t('unit_abbrev_weight_kilogram_metric') + ')'"
                                             max="907.18" min="0.45" name="weight" placeholder="0" step="0.01"
                                             :rules="[$zb.validators.required(), $zb.validators.betweenVal(0.45, 907.18)]"
                                             type="number"/>
                </div>
              </div>

              <!-- Avg. Bedtime -->
              <div v-if="showEditBedtime" class="row mb-48">
                <div class="col" data-testid="patientBedtime">

                  <!-- Bedtime Selection Display (<input>) -->
                  <zbm-input-with-validation v-model="displayBedtime" @on-click="showBedtimeModal = true"
                                             class="cursor-pointer" for="bedtime" :label="t('hbs_bedtime_title')"
                                             name="bedtime" :placeholder="defBedtime" :readonly="true"
                                             :rules="[$zb.validators.required()]"/>

                  <!-- Bedtime Selection Modal -->
                  <BedtimeSelectionModal v-model="showBedtimeModal" @selected-bedtime="updateModelBedtime"
                                         :default-bedtime="defBedtime" :model-bedtime="formModel.bedtime"/>
                </div>
              </div>

              <!-- Save -->
              <div class="row mt-auto">
                <div class="col">
                  <zbm-btn :color="$zb.enums.ButtonStdColorEnum.accent" data-testid="saveEditsButton"
                           :disable="!formIsDirty" :height="$zb.enums.ButtonHeightEnum.lg" :label="t('gen_save_edits')"
                           :type="$zb.enums.ButtonTypeEnum.submit" :width="$zb.enums.ButtonWidthEnum.wFull"/>
                </div>
              </div>
            </q-form>
          </template>
        </zbm-async-content>
      </template>
    </MultiViewLayout>
  </q-page>
</template>

<style scoped lang="scss">

// Bedtime Selection Display
:deep(.q-input.cursor-pointer) { // .q-field

  input {
    cursor: pointer;
  }
}
</style>
